import { Component, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/core/services/app.service';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { ImageCropperService } from 'src/app/core/services/image-cropper.service';
import { LocalDataStoreService } from 'src/app/core/services/local-data-store.service';
import { UserService } from 'src/app/core/services/user.service';
import { OOVenueService } from 'src/app/core/services/venue-service';
import { Check } from 'src/app/shared/models/check';
import { AppMessageSeverity } from 'src/app/shared/models/enums/AppMessageSeverity.enum';
import { CheckStatus } from 'src/app/shared/models/enums/CheckStatus.enum';

@Component({
	selector: 'app-checks-dashboard',
	templateUrl: './checks-dashboard.component.html',
	styleUrls: ['../../commandcenter.scss'],
})
export class ChecksDashboardComponent implements OnInit, OnDestroy {
	businessDate: string;

	checkList: Check[] = [];
	checkSelected: Check;
	checkViewType = [
		{
			label: 'Active Checks',
			value: 'active',
		},
		{
			label: 'Queued Checks',
			value: 'queue',
		},
	];
	checkViewSelected: string = 'active';

	checkSearchQueryParam: string = undefined;

	moment: any = moment;

	searchDisplay: boolean = false;

	venueChangeSubscription: Subscription;
	venueKey: number;

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private localStorage: LocalDataStoreService,
		private spinner: NgxSpinnerService,
		private userService: UserService,
		public imgCrop: ImageCropperService,
		public venueSvc: OOVenueService,
		private confirm: ConfirmationService
	) {}

	ngOnInit(): void {
		this.spinner.show();
		this.init();
	}

	ngOnDestroy() {
		try {
			this.venueChangeSubscription.unsubscribe();
		} catch (error) {}
	}

	async init() {
		const venue = this.venueSvc.subscriptionVenue.value;
		this.venueKey = venue.key;
		this.businessDate = moment().format('YYYY-MM-DD');
		await this.load();
	}

	async load() {
		if (this.venueSvc.ooVenue) {
			await this.checksGet(this.venueKey);
		}

		this.spinner.hide();
	}

	async businessDateChange(event) {
		this.businessDate = moment(event).format('YYYY-MM-DD');
		await this.refreshCheckView();
	}

	async changeCheckView(event) {
		this.checkViewSelected = event.value;
		await this.refreshCheckView();
	}

	async checksGetQueued(venueKey: number) {
		this.app.spinner.show();
		const resp = await this.focuslink.checksGetByParams(venueKey, `pos=false&status=0&minified=true`);
		this.checkList = resp.map((i) => {
			const chk = new Check(i);
			chk.source = this.app.references.checkSource(chk.source);
			return chk;
		});
		this.checkList.sort((a, b) => (moment(a.dueDateTime).unix() < moment(b.dueDateTime).unix() ? 1 : -1));
		this.app.spinner.hide();
	}

	async checksGet(venueKey: number, queryParams?: string) {
		this.app.loading(true);
		try {
			let resp;
			if (queryParams) {
				resp = await this.focuslink.checksGetByParams(this.venueKey, queryParams);
			} else {
				resp = await this.focuslink.checksGetByParams(venueKey, `businessDate=${this.businessDate}&minified=true`);
			}

			this.checkList = this.parseCheck(resp);
			this.checkList.sort((a, b) => (a.checkNumber > b.checkNumber ? 1 : -1));
		} catch (error) {
			this.app.msgSvc.add({ severity: AppMessageSeverity.RED, summary: '', detail: `No checks found.` });
		}
		this.app.loading(false);
	}

	async checkGet(venueKey: number, checkKey: string) {
		this.app.loading(true);
		const resp = await this.focuslink.getCheck(venueKey, checkKey);
		this.checkSelected = new Check(resp);
		this.app.loading(false);
	}

	async cancelUnsentCheck(checkKey: string) {
		this.app.confirmation.confirm({
			message: `Are you sure you want to cancel this Online Order? This action cannot be undone. The orderer will see an authorization on their payment type for up to seven days.`,
			accept: async () => {
				const check = this.checkList.find((i) => i.checkKey === checkKey);
				check.status = CheckStatus.CANCELLED;
				await this.focuslink.updateOrder(this.venueKey, checkKey, check);
			},
			reject: async () => {
				this.app.confirmation.close();
			},
		});
	}

	parseCheck(checkPayload): Check[] {
		return checkPayload.map((i) => {
			const chk = new Check(i);
			chk.source = this.app.references.checkSource(chk.source);
			chk.seats && (chk.seats = this.venueSvc.parseSeat(chk.seats));
			return chk;
		});
	}

	async refreshCheckView() {
		switch (this.checkViewSelected) {
			case 'active':
				await this.checksGet(this.venueKey);
				break;
			case 'queue':
				await this.checksGetQueued(this.venueKey);
				break;
			default:
				await this.checksGet(this.venueKey);
				break;
		}
	}

	async searchForCheck(checkSearchQueryParam: string) {
		this.searchDisplay = false;
		this.checksGet(this.venueKey, checkSearchQueryParam);
	}

	async sendOrder(checkKey: string) {
		const check = await this.focuslink.getCheck(this.venueKey, checkKey);
		this.app.confirmation.confirm({
			message: `Send order early? The guests originally requested due date was ${moment(check.dueDateTime).fromNow()}`,
			accept: async () => {
				const tempCheck = {
					checkKey: check.checkKey,
					dueDateTime: moment.utc().format(),
					queueDateTime: moment.utc().format(),
				};
				const res = await this.focuslink.checkPatch(this.venueKey, tempCheck.checkKey, tempCheck);
				const existingIdx = this.checkList.findIndex((i) => i.checkKey === checkKey);
				this.checkList[existingIdx] = new Check(res);
			},
			reject: async () => {
				this.app.confirmation.close();
			},
		});
	}
}
